import apisauce, { ApiOkResponse } from 'apisauce'
import Config from '../../Config'
import { isMobile } from 'APP/Helpers/checkPlatform'
import { logger } from 'APP/Lib/Datadog'
import { camelizeKeys } from 'humps'
import type {
  AddFamilyMemberRequest,
  AddFamilyMemberResponse,
  ChallengeForm,
  CollectionAddFamilyMemberResponse,
  CollectionInvitationChallengedResponse,
  CollectionInvitationUnchallengedResponse,
  CollectionLookupOrganization,
  CollectionUpdateFamilyMemberStatusResponse,
  ExpandedUserResource,
  ExtendedEligibleIntervalResource,
  InvitationChallengedResponse,
  InvitationChallengeResponse,
  InvitationUnchallengedResponse,
  InvitationValidationResponse,
  ItemInvitationChallengeResponse,
  ItemUserEnrolmentResponse,
  ItemExpandedUserResource,
  LookupOrganization,
  ModelError,
  PaginatedExtendedEligibleIntervalResource,
  ReducedEligibilityRecord,
  UpdateFamilyMemberStatusRequest,
  UserEnrolByEnrolmentCodeRequest,
  UserEnrolByOrganizationIdRequest,
  UserOrganization,
  InvitationResendEmailRequest,
} from '@dialogue/scribe'
import type {
  NormalizedChallengeForm,
  NormalizedChallengedInvitation,
  NormalizedEligibilityRecord,
  NormalizedEnrolByEnrolmentCodeResponse,
  NormalizedOrganization,
  NormalizedUserV2,
  NormalizedFamilyMember,
} from './types'
import type { ScribeV1 } from './v1.types'
import type { HealthResources } from 'APP/Store/Patient/types'

type ApiError = {
  id: string
  status: string
  title: string
  detail: string
  code: string
  source: unknown | null
}

type ApiErrors = {
  errors: ApiError[]
}

type FileObj = {
  fileUri?: string
  file?: File
}

type UpsertChildData = {
  givenName: string
  preferredName: string
  pronouns: string
  familyName: string
  dateOfBirth: string
  genderIdentity: string
  genderIdentityDescription: string
}

const create = (loginStore: { accessToken: string }, baseURL = Config.SCRIBE_DOMAIN) => {
  const authToken = loginStore.accessToken
  const api = apisauce.create({
    baseURL,
    headers: {
      'Cache-Control': 'no-cache',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
    timeout: 20000,
  })

  // URL Builders
  const getScribeV2UserUrl = (userId: string) => `/v2/users/${userId}`
  const getScribeV2UserEligibilitiesUrl = (userId: string) =>
    `/v2/users/${userId}/eligible_intervals`
  const enrolWithEnrolmentCodeUrl = (userId: string) =>
    `/v2/users/${userId}/enrol_by_enrolment_code`
  const resendInvitationUrl = `/v2/invitations/resend`
  const lookupOrganizationUrl = `/v2/organizations/lookup`
  const userAccessLimits = `/v2/users/rate_limits`
  const buildRegisterCardUrl = (userId: string) => `/v1/users/${userId}/stripe_registrations`
  const buildChargeCardUrl = (chargeId: string) => `/v1/charges/${chargeId}capture`
  const buildFamilyMembersUrl = (userId: string, brandVersion?: string) => {
    const baseUrl = `/v1/users/${userId}/family_members`
    return brandVersion ? `${baseUrl}?brand_version=${brandVersion}` : baseUrl
  }
  const buildFamilyMemberUrl = (familyId: string, memberId: string) =>
    `/v1/families/${familyId}/members/${memberId}`
  const buildV2FamilyMemberUrl = (userId: string, familyId?: string, brandVersion?: string) => {
    const base = familyId
      ? `/v2/users/${userId}/family_members/${familyId}`
      : `/v2/users/${userId}/family_members`
    return brandVersion ? `${base}?brand_version=${brandVersion}` : base
  }
  const buildChildUrl = (userId: string) => `/v1/users/${userId}/children`
  const buildUnchallengedInvitationSearchUrl = (
    firstName: string,
    lastName: string,
    dateOfBirth: string,
    brandId?: string
  ) =>
    // Omitting brand_id searches invitations across all brands
    `/v2/invitations/unchallenged?${new URLSearchParams({
      first_name: firstName,
      last_name: lastName,
      date_of_birth: dateOfBirth,
      ...(brandId !== undefined ? { brand_id: brandId } : {}),
    }).toString()}`
  const buildChallengedInvitationSearchUrl = (
    firstName: string,
    lastName: string,
    dateOfBirth: string,
    brandId: string
  ) =>
    `/v2/invitations/challenged?${new URLSearchParams({
      first_name: firstName,
      last_name: lastName,
      date_of_birth: dateOfBirth,
      brand_id: brandId,
    }).toString()}`
  const buildChallengeInvitationUrl = (invitationId: string) =>
    `/v2/invitations/${invitationId}/challenge`
  const v2UsersEnsureBrandEligibilityUrl = (userId: string) =>
    `/v2/users/${userId}/ensure_brand_eligibility`
  const v2UsersEnrolByOrganizationIdUrl = (userId: string) =>
    `/v2/users/${userId}/enrol_by_organization`

  // Transforms
  const normalizeFamily = (
    rawFamily: ScribeV1.FamilyMemberCollection['data']
  ): NormalizedFamilyMember[] => rawFamily.map((member) => normalizeFamilyMember(member))

  const normalizeFamilyId = (relationships: ScribeV1.FamilyMember['relationships']) =>
    relationships.family && relationships.family.data && relationships.family.data.id

  const normalizeFamilyMember = (
    rawFamilyMember: ScribeV1.FamilyMember
  ): NormalizedFamilyMember => ({
    id: rawFamilyMember.id,
    email: rawFamilyMember.attributes.email,
    givenName: rawFamilyMember.attributes.first_name,
    preferredName: rawFamilyMember.attributes.preferred_name,
    pronouns: rawFamilyMember.attributes.pronouns,
    genderIdentity: rawFamilyMember.attributes.gender_identity,
    genderIdentityDescription: rawFamilyMember.attributes.gender_identity_description,
    familyName: rawFamilyMember.attributes.last_name,
    birthdate: rawFamilyMember.attributes.birthdate,
    picture: rawFamilyMember.attributes.picture,
    type: rawFamilyMember.attributes.type,
    authId: rawFamilyMember.attributes.auth_id,
    status: rawFamilyMember.attributes.status,
    healthcardUrl:
      rawFamilyMember.links &&
      rawFamilyMember.links.healthcard &&
      rawFamilyMember.links.healthcard.href,
    permissions:
      rawFamilyMember.links &&
      rawFamilyMember.links.self &&
      rawFamilyMember.links.self.meta &&
      rawFamilyMember.links.self.meta.methods &&
      (rawFamilyMember.links.self.meta.methods.includes('delete') // usually for invited members
        ? 'delete'
        : rawFamilyMember.links.self.meta.methods.includes('put') // usually only for children
        ? 'put'
        : 'none'),
  })

  const normalizeAddFamilyMemberV2Response = (
    raw: AddFamilyMemberResponse,
    email: string
  ): NormalizedFamilyMember => ({
    id: String(raw?.user_id),
    email,
    givenName: null,
    preferredName: null,
    pronouns: null,
    genderIdentity: null,
    genderIdentityDescription: null,
    familyName: null,
    birthdate: null,
    picture: null,
    // @ts-expect-error FIXME: Scribe's AddFamilyMemberResponse Pydantic model represents this as a string, not the FamilyMemberType enum
    type: raw?.type,
    authId: null,
    permissions: 'delete',
    status: 'pending',
  })

  const normalizeFamilyPermissions = (
    links: ScribeV1.FamilyMember['links']
  ): { children: 'create' | 'read'; dependents: 'create' | 'read' } => ({
    children:
      links &&
      links.children &&
      links.children.meta &&
      links.children.meta.methods &&
      links.children.meta.methods.includes('post')
        ? 'create'
        : 'read',
    dependents:
      links &&
      links.dependents &&
      links.dependents.meta &&
      links.dependents.meta.methods &&
      links.dependents.meta.methods.includes('post')
        ? 'create'
        : 'read',
  })

  const normalizeUserV2 = (rawUser: ExpandedUserResource): NormalizedUserV2 => {
    const authId = rawUser.auth_id
    const contentTags = rawUser.content_tags?.map((ct) => ct?.name) || []
    const creditCard = rawUser.credit_card ? rawUser.credit_card : null
    const eligibleServices = rawUser.eligible_services?.[Config.BRAND_ID] || {}
    const eligibleBrands = Object.keys(rawUser.eligible_services || [])
    const healthResources = camelizeKeys(rawUser.health_resources) as HealthResources
    const hasFamilyAccess = Object.values(eligibleServices).some(
      (service) => service?.family_access !== 'none'
    )
    const cobrands = rawUser.cobrands || []
    return {
      healthResources,
      contentTags,
      creditCard,
      eligibleServices,
      eligibleBrands,
      hasFamilyAccess,
      cobrands,
      authId,
    }
  }

  // Currently only used for org level forced MFA, but this could be used for other things in the future
  const normalizeUserV2Eligibilities = (rawEligibilities: ExtendedEligibleIntervalResource[]) => {
    const isMfaRequired = rawEligibilities.some(
      (interval) =>
        interval?.status === 'active' &&
        (interval?.eligibility_record?.organization?.forced_mfa ?? false)
    )
    // This should work only for mobile as for a desktop version we have checkPublicComputer action.
    const isForcedSessionInactivityRequired =
      isMobile() &&
      rawEligibilities.some(
        (interval) =>
          interval?.status === 'active' &&
          (interval?.eligibility_record?.organization?.forced_mobile_session_inactivity ?? false)
      )
    const eligibilityIntervals = rawEligibilities
      .filter(
        // We don't filter by brand at the request to get an accurate forced MFA value but we do want to
        // filter eligibility intervals that are used by the app
        (interval) => interval?.eligibility_record?.organization?.brand_id === Config.BRAND_ID
      )
      .map((interval) => {
        return {
          id: interval?.id,
          planId: interval?.plan_id,
          startDate: interval?.start_date,
          endDate: interval?.end_date,
          status: interval?.status,
          eligibilityRecord: normalizeEligibilityRecord(interval?.eligibility_record),
        }
      })
    return { isMfaRequired, isForcedSessionInactivityRequired, eligibilityIntervals }
  }

  const normalizeOrganization = (rawOrganization: UserOrganization): NormalizedOrganization => {
    return {
      id: rawOrganization.id,
      careTeamNotes: rawOrganization?.care_team_notes,
      displayName: {
        en: rawOrganization?.display_name?.en ?? rawOrganization?.name,
        fr: rawOrganization?.display_name?.fr ?? rawOrganization?.name,
      },
      forcedMfa: rawOrganization?.forced_mfa,
      forcedSessionInactivity: rawOrganization?.forced_mobile_session_inactivity,
      name: rawOrganization?.name,
    }
  }

  const normalizeEligibilityRecord = (
    rawEligibilityRecord: ReducedEligibilityRecord
  ): NormalizedEligibilityRecord => {
    return {
      id: rawEligibilityRecord.id,
      organization: normalizeOrganization(rawEligibilityRecord?.organization),
      participantId: rawEligibilityRecord.participant_id,
    }
  }

  const normalizeChallengeForm = (rawChallengeForm: ChallengeForm): NormalizedChallengeForm => {
    return {
      title: rawChallengeForm?.title,
      body: rawChallengeForm?.body,
      fieldsOperation: rawChallengeForm?.fields_operation,
      challengeAttribute: rawChallengeForm?.challenge_attribute,
      fields: rawChallengeForm?.fields?.map?.((field) => {
        return {
          hint: field?.hint,
          infoTip: field?.info_tip,
          invalidError: field?.invalid_error,
          label: field?.label,
          requiredError: field?.required_error,
          type: field?.type,
          operation: field?.operation,
        }
      }),
    }
  }

  const normalizeChallengedInvitation = (
    rawInvitation: InvitationChallengedResponse
  ): NormalizedChallengedInvitation => {
    return {
      id: rawInvitation?.id,
      challengeForm: normalizeChallengeForm(rawInvitation?.challenge_form),
      organization: normalizeOrganization(rawInvitation?.organization),
    }
  }

  // API
  const getScribeV2User = (userId: string, params: Record<string, unknown>) => {
    return api
      .get<ItemExpandedUserResource, ApiErrors>(getScribeV2UserUrl(userId), params)
      .then((response) => {
        if (response.ok) {
          if (response.data) {
            return normalizeUserV2(response.data.data)
          } else {
            throw (
              //@ts-expect-error response.data is not undefined
              response.data.errors[0] || {
                status: response.status,
                title: 'Empty response body',
                detail: 'Missing response body',
              }
            )
          }
        } else {
          throw (
            //@ts-expect-error response.data is not undefined
            response.data.errors[0] || {
              status: response.status,
              title: 'Empty error body',
              detail: 'none',
            }
          )
        }
      })
  }

  const getScribeV2UserEligibilities = (userId: string) => {
    return api
      .get<PaginatedExtendedEligibleIntervalResource, ApiErrors>(
        getScribeV2UserEligibilitiesUrl(userId)
      )
      .then((response) => {
        if (response.ok) {
          if (response.data) {
            return normalizeUserV2Eligibilities(response.data.data)
          } else {
            throw (
              //@ts-expect-error response.data is not undefined
              response.data.errors[0] || {
                status: response.status,
                title: 'Empty response body',
                detail: 'Missing response body',
              }
            )
          }
        } else {
          throw (
            //@ts-expect-error response.data is not undefined
            response.data.errors[0] || {
              status: response.status,
              title: 'Empty error body',
              detail: 'none',
            }
          )
        }
      })
  }

  const registerCreditCard = (userId: string, creditCardToken: string) => {
    return api
      .post<ScribeV1.RegisterCreditCardResponse, ApiErrors>(buildRegisterCardUrl(userId), {
        data: {
          type: 'stripe_registration',
          attributes: {
            token: creditCardToken,
          },
        },
      })
      .then((response) => {
        if (response.ok) {
          if (response.data && response.data.data && response.data.data.attributes) {
            return response.data.data.attributes
          } else {
            throw (
              //@ts-expect-error response.data is not undefined
              response.data.errors[0] || {
                status: response.status,
                title: 'Incomplete response body',
                detail: 'Missing "attributes" key in response body',
              }
            )
          }
        } else {
          throw (
            //@ts-expect-error response.data is not undefined
            response.data.errors[0] || {
              status: response.status,
              title: 'Empty error body',
              detail: 'none',
            }
          )
        }
      })
  }

  const chargeCreditCard = (chargeId: string) => {
    return api
      .post<ScribeV1.ChargeCreditCardResponse, ApiErrors>(buildChargeCardUrl(chargeId))
      .then((response) => {
        if (response.ok && response.data) {
          return response.data.data
        } else {
          throw (
            //@ts-expect-error response.data is not undefined
            response.data.errors[0] || {
              status: response.status,
              title: 'Empty error body',
              detail: 'none',
            }
          )
        }
      })
  }

  const getFamilyMembers = (userId: string) => {
    return api
      .get<ScribeV1.FamilyMemberCollection, ApiErrors>(buildFamilyMembersUrl(userId))
      .then((response) => {
        if (response.ok) {
          if (response.data) {
            const data = response.data
            const familyId = normalizeFamilyId(data.relationships)
            const family = normalizeFamily(data.data)
            const permissions = normalizeFamilyPermissions(data.links)
            return {
              familyId,
              family,
              permissions,
            }
          }
        }
        throw (
          // @ts-expect-error FIXME: type narrowing is insufficient to guarantee response.data.errors is defined here
          response.data.errors[0] || {
            status: response.status,
            title: 'Empty error body',
            detail: 'none',
          }
        )
      })
  }

  const addAdult = async (userId: string, emailToInvite: string, brandVersion?: string) => {
    const body: AddFamilyMemberRequest = {
      type: 'guardian',
      email: emailToInvite,
    }
    const response = await api.post<CollectionAddFamilyMemberResponse, ApiError>(
      buildV2FamilyMemberUrl(userId, undefined, brandVersion),
      body
    )
    if (response.ok && response.data) {
      const member = response.data.data[0]
      const familyId = member?.family_id ?? null
      return {
        member: normalizeAddFamilyMemberV2Response(member, emailToInvite),
        familyId,
      }
    }

    const errorResponse = response.data as ApiError
    const errorMessage = errorResponse.detail || 'Empty error body'
    logger.error(errorMessage)
    throw new Error(errorMessage)
  }

  const removeAdult = async (familyId: string, memberId: string) => {
    const response = await api.delete<ScribeV1.FamilyMemberResource, ApiErrors>(
      buildFamilyMemberUrl(familyId, memberId)
    )

    if (!response.ok) {
      const errorResponses = response?.data as ApiErrors
      const errorMessage = errorResponses.errors[0].detail || 'Empty error body'

      logger.error(errorMessage)

      throw new Error(errorMessage, { cause: { response } })
    }
  }

  const acceptFamilyInvite = (
    userId: string,
    familyId: string,
    // TODO: discuss with Client Stream if Scribe should implement enum validation on the status string here
    request: UpdateFamilyMemberStatusRequest
  ) => {
    return api
      .patch<CollectionUpdateFamilyMemberStatusResponse, ApiErrors>(
        buildV2FamilyMemberUrl(userId, familyId),
        request
      )
      .then((response) => {
        if (response.ok && response.data) {
          return response.data.data
        }
        const errorResponse = response.data as ApiErrors | undefined
        throw (
          errorResponse?.errors?.[0] || {
            id: '',
            status: String(response.status),
            title: 'Empty error body',
            detail: 'none',
            code: '',
            source: null,
          }
        )
      })
  }

  const addChild = (userId: string, child: UpsertChildData) => {
    const { givenName, preferredName, pronouns, familyName, dateOfBirth } = child
    return api
      .post<ScribeV1.FamilyMemberResource, ApiErrors>(buildChildUrl(userId), {
        data: {
          type: 'child',
          attributes: {
            create_health_card_url: false, // DIA-62447 - We don't want to create a health card for the child yet
            first_name: givenName,
            preferred_name: preferredName,
            pronouns: pronouns,
            last_name: familyName,
            birthdate: dateOfBirth,
          },
        },
      })
      .then((response) => {
        if (response.ok) {
          if (response.data) {
            if (
              response.data.data &&
              response.data.data.attributes &&
              response.data.data.links &&
              response.data.data.links.healthcard
            ) {
              return {
                child: normalizeFamilyMember(response.data.data),
                url: response.data.data.links.healthcard.meta.post_info.url,
                fields: response.data.data.links.healthcard.meta.post_info.fields,
                familyId: normalizeFamilyId(response.data.data.relationships),
              }
            }
          }
        }
        throw (
          // @ts-expect-error FIXME: type narrowing is insufficient to guarantee response.data.errors is defined here
          response.data.errors[0] || {
            status: response.status,
            title: 'Empty error body',
            detail: 'none',
          }
        )
      })
  }

  // Note: This is unfortunately not the same code path as updaing a user's id.
  // We should probably consolidate this code replication
  const uploadChildIdCard = (url: string, fields: Record<string, unknown>, fileObj: FileObj) => {
    let path = fileObj?.fileUri || ''
    if (
      fileObj?.fileUri?.indexOf('content://') === -1 &&
      fileObj?.fileUri?.indexOf('file://') === -1
    ) {
      path = `file://${path}`
    }

    const photo = fileObj?.file
      ? fileObj.file
      : {
          uri: path,
          type: 'image/jpeg',
          name: 'photo.jpg',
        }

    let body = new FormData()

    Object.keys(fields).forEach((key) => {
      body.append(key, fields[key] as string)
    })
    body.append('file', photo as File)

    const aws = apisauce.create({
      baseURL: url,
      headers: {
        'Cache-Control': 'no-cache',
        'Content-Type': 'multipart/form-data',
      },
      timeout: 20000,
    })

    return aws.post(url, body).then((response) => {
      if (response.ok) return response
      throw (
        //@ts-expect-error response.data is not undefined
        (response.data && response.data.errors && response.data.errors[0]) || {
          status: response.status,
          title: 'Empty error body',
          detail: 'none',
        }
      )
    })
  }

  const updateChild = (familyId: string, memberId: string, child: UpsertChildData) => {
    const {
      givenName,
      preferredName,
      pronouns,
      familyName,
      dateOfBirth,
      genderIdentity,
      genderIdentityDescription,
    } = child
    return api
      .patch<ScribeV1.FamilyMemberResource, ApiErrors>(buildFamilyMemberUrl(familyId, memberId), {
        data: {
          type: 'child',
          attributes: {
            first_name: givenName,
            preferred_name: preferredName,
            pronouns: pronouns,
            last_name: familyName,
            birthdate: dateOfBirth,
            gender_identity: genderIdentity,
            gender_identity_description: genderIdentityDescription,
          },
        },
      })
      .then((response) => {
        if (response.ok) {
          if (response.data) {
            if (
              response.data.data &&
              response.data.data.attributes &&
              response.data.data.links &&
              response.data.data.links.healthcard
            ) {
              return {
                child: normalizeFamilyMember(response.data.data),
                url: response.data.data.links.healthcard.meta.post_info.url,
                fields: response.data.data.links.healthcard.meta.post_info.fields,
              }
            }
          }
        }
        throw (
          // @ts-expect-error FIXME: type narrowing is insufficient to guarantee response.data.errors is defined here
          response.data.errors[0] || {
            status: response.status,
            title: 'Empty error body',
            detail: 'none',
          }
        )
      })
  }

  const enrolWithEnrolmentCode = (
    userId: string,
    code: string,
    brandId: string
  ): Promise<NormalizedEnrolByEnrolmentCodeResponse | undefined> => {
    const body: UserEnrolByEnrolmentCodeRequest = {
      enrolment_code: code,
      brand_id: brandId,
    }
    return api
      .post<ItemUserEnrolmentResponse, ApiErrors>(enrolWithEnrolmentCodeUrl(userId), body)
      .then((response) => {
        if (response.ok && response.data) {
          return {
            profile: normalizeUserV2(response?.data?.data?.user),
            organization: normalizeEligibilityRecord(response?.data?.data?.eligibility_record)
              ?.organization,
          }
        }

        let error
        if (response.data) {
          //@ts-expect-error response.data is not undefined
          error = response.data.errors
            ? //@ts-expect-error response.data is not undefined
              response.data.errors[0]
            : {
                status: response.status,
                title: 'Unable to enrol',
              }
        } else {
          error = { status: response.problem }
        }
        throw error
      })
  }

  const ensureBrandEligibility = async (userId: string, brandId: string): Promise<void> => {
    // FIXME: confirm with Client Stream if the endpoint actually accepts a payload
    // It seems to take the brand ID from claims today: https://github.com/dialoguemd/scribe/blob/6ff7c11fd91951536faa9f9822e41477e0be8a47/v2/routers/users/enrol.py#L539
    const response = await api.post<boolean, ApiErrors>(v2UsersEnsureBrandEligibilityUrl(userId), {
      brand_id: brandId,
    })

    if (response.ok) return

    // Error Handling?
    let error

    if (response.data) {
      error = response.data.errors
        ? response.data.errors[0]
        : {
            status: response.status,
          }
    } else {
      error = { status: response.problem }
    }

    throw error
  }

  const enrolByOrganizationId = async (userId: string, organizationId: number): Promise<void> => {
    const body: UserEnrolByOrganizationIdRequest = { organization_id: organizationId }
    const response = await api.post<boolean, ApiErrors>(
      v2UsersEnrolByOrganizationIdUrl(userId),
      body
    )

    if (response.ok) return

    const error = response.data?.errors?.[0] || {
      status: response.status || response.problem,
      title: 'Unable to enrol with organization_id',
    }

    throw error
  }

  const unchallengedInvitationSearch = (
    firstName: string,
    lastName: string,
    dateOfBirth: string,
    brandId?: string
  ): Promise<InvitationUnchallengedResponse[] | undefined> => {
    return api
      .get<CollectionInvitationUnchallengedResponse, ApiErrors>(
        buildUnchallengedInvitationSearchUrl(firstName, lastName, dateOfBirth, brandId)
      )
      .then((response) => {
        if (response.ok) {
          return response?.data?.data
        }
        throw (
          //@ts-expect-error response.data is not undefined
          response.data.errors || {
            status: response.status,
            title: 'Empty error body',
            detail: 'none',
          }
        )
      })
  }

  const challengedInvitationSearch = (
    firstName: string,
    lastName: string,
    dateOfBirth: string,
    brandId: string
  ): Promise<NormalizedChallengedInvitation[] | undefined> => {
    return api
      .get<CollectionInvitationChallengedResponse, ApiErrors>(
        buildChallengedInvitationSearchUrl(firstName, lastName, dateOfBirth, brandId)
      )
      .then((response) => {
        if (response.ok) {
          return response?.data?.data?.map?.((invite) => normalizeChallengedInvitation(invite))
        }

        throw (
          response.data?.errors || {
            status: response.status,
            title: 'Empty error body',
            detail: 'none',
          }
        )
      })
  }

  const challengeInvitation = (
    invitationId: string,
    // FIXME: Scribe v2 doesn't expose a type for this
    // https://github.com/dialoguemd/scribe/blob/6ff7c11fd91951536faa9f9822e41477e0be8a47/v2/routers/invitations/challenge_invitation/handlers.py#L13
    attribute: Record<string, string | null>
  ): Promise<InvitationChallengeResponse | undefined> => {
    return api
      .post<ItemInvitationChallengeResponse, ModelError>(
        buildChallengeInvitationUrl(invitationId),
        attribute
      )
      .then((response) => {
        if (response.ok) {
          return response?.data?.data
        }
        throw (
          response?.data?.detail ?? {
            status: response.status,
            title: 'Failed challenge',
          }
        )
      })
  }

  const lookupOrganizations = (name: string): Promise<LookupOrganization[] | undefined> => {
    return api
      .get<CollectionLookupOrganization, ApiErrors>(lookupOrganizationUrl, { name })
      .then((response) => {
        if (response.ok) {
          return response.data?.data
        }
        throw response.data?.errors?.[0] ?? { status: response.status, title: 'Lookup failed' }
      })
  }

  const resendInvitation = (
    email: string,
    brandId: string
  ): Promise<ApiOkResponse<InvitationValidationResponse | undefined>> => {
    const body: InvitationResendEmailRequest = {
      email,
      brand_id: brandId,
    }
    return api
      .post<InvitationValidationResponse, ApiErrors>(resendInvitationUrl, body)
      .then((response) => {
        if (response.ok) {
          return response
        }
        //@ts-expect-error response.data is not undefined if response.ok is true
        const errors = response.data.errors
        const error = errors
          ? errors[0]
          : {
              status: response.status,
              title: 'Unable to resend invitation',
            }
        throw error
      })
  }

  // FIXME: Scribe v2 doesn't expose a type for this
  // https://github.com/dialoguemd/scribe/blob/6ff7c11fd91951536faa9f9822e41477e0be8a47/v2/routers/users/rate_limits.py#L43
  type LookupOrganizationAttempts = Record<string, { remaining: number }[]>
  const key = 'get:/v2/organizations/lookup'

  const hasRemainingOrganizationSearchRetries = (): Promise<boolean | undefined> => {
    return api
      .get<LookupOrganizationAttempts, ApiErrors>(userAccessLimits, {
        keys: key,
      })
      .then((response) => {
        if (!response.ok || !response.data || 'errors' in response.data) {
          throw (
            response.data?.errors?.[0] ?? {
              status: response.status,
              title: 'Error checking remaining attempts on org search retries',
            }
          )
        }

        const remainingAttempts = response.data[key]?.[0]?.remaining ?? 0
        // Member has a limit of 3 attempts
        return remainingAttempts > 0
      })
  }

  return {
    getScribeV2User,
    getScribeV2UserEligibilities,
    registerCreditCard,
    chargeCreditCard,
    getFamilyMembers,
    addAdult,
    removeAdult,
    acceptFamilyInvite,
    addChild,
    uploadChildIdCard,
    updateChild,
    enrolWithEnrolmentCode,
    ensureBrandEligibility,
    unchallengedInvitationSearch,
    challengedInvitationSearch,
    resendInvitation,
    challengeInvitation,
    lookupOrganizations,
    enrolByOrganizationId,
    hasRemainingOrganizationSearchRetries,
  }
}

export default {
  create,
}
